import { elementBounds } from "./constants";

const MARGIN = 24;

export function regionOf(elements, pad = MARGIN) {
  const boxes = elements
    .map(elementBounds)
    .filter((b) => b && Number.isFinite(b.x) && Number.isFinite(b.y)
      && Number.isFinite(b.w) && Number.isFinite(b.h));
  if (!boxes.length) return null;
  const x1 = Math.min(...boxes.map((b) => b.x));
  const y1 = Math.min(...boxes.map((b) => b.y));
  const x2 = Math.max(...boxes.map((b) => b.x + b.w));
  const y2 = Math.max(...boxes.map((b) => b.y + b.h));
  return { x: x1 - pad, y: y1 - pad, w: x2 - x1 + pad * 2, h: y2 - y1 + pad * 2 };
}

async function inlineImages(svgRoot) {
  const images = Array.from(svgRoot.querySelectorAll("image"));
  await Promise.all(images.map(async (node) => {
    const href = node.getAttribute("href") || node.getAttribute("xlink:href");
    if (!href || href.startsWith("data:")) return;
    try {
      const res = await fetch(href, { mode: "cors" });
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      node.setAttribute("href", dataUrl);
      node.removeAttribute("xlink:href");
    } catch (err) {
      console.warn("[whiteboard] export could not inline an image:", err?.message || err);
      node.remove();
    }
  }));
}

export async function renderRegionToCanvas(svgEl, region, { scale = 2, background = "#F8F6F1" } = {}) {
  if (!svgEl || !region) return null;
  if (![region.x, region.y, region.w, region.h].every(Number.isFinite)
      || region.w <= 0 || region.h <= 0) {
    throw new Error("that region cannot be exported");
  }

  const clone = svgEl.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", region.w);
  clone.setAttribute("height", region.h);
  clone.setAttribute("viewBox", `${region.x} ${region.y} ${region.w} ${region.h}`);

  const root = clone.querySelector("g");
  if (root) root.removeAttribute("transform");

  clone.querySelectorAll("[data-export-ignore]").forEach((n) => n.remove());
  clone.querySelectorAll("foreignObject").forEach((n) => n.remove());

  await inlineImages(clone);

  const markup = new XMLSerializer().serializeToString(clone);
  const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("could not rasterise the board"));
    image.src = svgUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(region.w * scale));
  canvas.height = Math.max(1, Math.round(region.h * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export async function regionToPngBlob(svgEl, region, options) {
  const canvas = await renderRegionToCanvas(svgEl, region, options);
  if (!canvas) return null;
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export async function regionToDataUrl(svgEl, region, options) {
  const canvas = await renderRegionToCanvas(svgEl, region, options);
  return canvas ? canvas.toDataURL("image/png") : null;
}

export function imageToPdfBlob(jpegDataUrl, imgPxW, imgPxH, pageW, pageH) {
  const base64 = String(jpegDataUrl).split(",")[1] || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

  const w = Math.max(1, Math.round(pageW));
  const h = Math.max(1, Math.round(pageH));
  const enc = new TextEncoder();
  const chunks = [];
  const offsets = [];
  let length = 0;

  const push = (data) => {
    const buf = typeof data === "string" ? enc.encode(data) : data;
    chunks.push(buf);
    length += buf.length;
  };

  push("%PDF-1.4\n");

  offsets.push(length);
  push("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");

  offsets.push(length);
  push("2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");

  offsets.push(length);
  push(`3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] `
    + "/Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>\nendobj\n");

  const content = `q\n${w} 0 0 ${h} 0 0 cm\n/Im0 Do\nQ\n`;
  offsets.push(length);
  push(`4 0 obj\n<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`);

  offsets.push(length);
  push(`5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${Math.round(imgPxW)} `
    + `/Height ${Math.round(imgPxH)} /ColorSpace /DeviceRGB /BitsPerComponent 8 `
    + `/Filter /DCTDecode /Length ${bytes.length} >>\nstream\n`);
  push(bytes);
  push("\nendstream\nendobj\n");

  const xrefAt = length;
  let xref = `xref\n0 ${offsets.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((off) => { xref += `${String(off).padStart(10, "0")} 00000 n \n`; });
  push(xref);
  push(`trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`);

  return new Blob(chunks, { type: "application/pdf" });
}

export async function regionToJpegDataUrl(svgEl, region, options) {
  const canvas = await renderRegionToCanvas(svgEl, region, options);
  if (!canvas) return null;
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.92), width: canvas.width, height: canvas.height };
}

export function downloadBlob(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const SNAPSHOT_WIDTH = 1280;
export const SNAPSHOT_HEIGHT = 720;

export function fitRegionToAspect(region, aspect) {
  if (!region || !(aspect > 0)) return region;
  const current = region.w / region.h;
  if (current > aspect) {
    const h = region.w / aspect;
    return { x: region.x, y: region.y - (h - region.h) / 2, w: region.w, h };
  }
  const w = region.h * aspect;
  return { x: region.x - (w - region.w) / 2, y: region.y, w, h: region.h };
}

export async function renderBoardSnapshot(svgEl, elements, {
  width = SNAPSHOT_WIDTH, height = SNAPSHOT_HEIGHT, quality = 0.82, background = "#F8F6F1",
} = {}) {
  const drawable = (elements || []).filter((el) => el && el.type !== "connector");
  const content = regionOf(drawable, 40);
  if (!content) return "";
  const region = fitRegionToAspect(content, width / height);
  const canvas = await renderRegionToCanvas(svgEl, region, { scale: width / region.w, background });
  if (!canvas) return "";
  if (canvas.width === width && canvas.height === height) return canvas.toDataURL("image/jpeg", quality);
  const exact = document.createElement("canvas");
  exact.width = width;
  exact.height = height;
  exact.getContext("2d").drawImage(canvas, 0, 0, width, height);
  return exact.toDataURL("image/jpeg", quality);
}
